/**
 * UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2) -- the ONE
 * role-presentation owner.
 *
 * Pure module (no React, no I/O) so the root vitest suite can import it for
 * the equivalence pin. Maps the backend `UserRole` union (mirrored on
 * `DashboardMe['role']`) to the human label the shell chrome renders. This is
 * the "role presentation" experience rule: a role is shown as its ONE human
 * label, resolved from `me.role` (server truth), never inferred from the URL,
 * the nav sections, or a name -- and it NEVER widens authority (a label is a
 * label; the SELECT-level fences + route guards remain the boundary).
 *
 * The labels follow the CLAUDE.md product names (Owner / Doctor / COC / ...)
 * and the sidebar section headers already in the shell (receptionist renders
 * as "Front desk", matching `ReceptionistNav`'s section label), so the chrome
 * reads as ONE product.
 */

import type { DashboardMe } from '@/dashboard/types';

export type PresentableRole = DashboardMe['role'];

/**
 * Human label per role. Exhaustive over the dashboard role union; a compile error
 * fires here if a role is ever added to `DashboardMe['role']` without a label.
 */
const ROLE_LABELS: Record<PresentableRole, string> = {
  owner: 'Owner',
  gm_agm: 'AGM / GM',
  regional_head: 'Regional Head',
  coc: 'COC',
  doctor: 'Doctor',
  // Matches the sidebar section header (ReceptionistNav renders "Front desk").
  receptionist: 'Front desk',
  call_center_agent: 'Call center',
  call_center_manager: 'Call centre manager',
  patient: 'Patient',
};

/** The human label for a role. Falls back to a readable de-cased token for an
 *  unknown role rather than throwing (defensive against an older /me wire). */
export function roleLabel(role: PresentableRole | string | null | undefined): string {
  if (role == null) return '';
  if (Object.prototype.hasOwnProperty.call(ROLE_LABELS, role)) {
    return ROLE_LABELS[role as PresentableRole];
  }
  // Unknown token: de-case snake_case into a readable label, never a raw id.
  return String(role)
    .split('_')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/** The full set of presentable role keys (for exhaustive tests / iteration). */
export const PRESENTABLE_ROLES = Object.keys(ROLE_LABELS) as PresentableRole[];
